import {
  FaroMetaAttributesSpanProcessor,
  FaroTraceExporter,
  FaroUserActionSpanProcessor,
} from '@grafana/faro-web-tracing';
import { context, Span, trace } from '@opentelemetry/api';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ReadableSpan, SpanProcessor } from '@opentelemetry/sdk-trace-base';
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import isNil from 'lodash/isNil';
import { faro } from './faro';

const isOtlpEnabled = import.meta.env.VITE_ENABLE_TRACING === 'true';
const isFaroEnabled = !isNil(faro);

export function isTracingEnabled() {
  return isOtlpEnabled || isFaroEnabled;
}

let customAttributes: { userId?: string; orgId?: string } = {};

/**
 * Custom span processor that adds user_id and org_id attributes to all spans
 */
class CustomAttributesSpanProcessor implements SpanProcessor {
  onStart(span: Span): void {
    if (customAttributes.userId) {
      span.setAttribute('user.id', customAttributes.userId);
    }
    if (customAttributes.orgId) {
      span.setAttribute('org.id', customAttributes.orgId);
    }
  }

  onEnd(_span: ReadableSpan): void {
    // No-op
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
}

export function updateTracingUserAttributes(userId: string | number | null, orgId: string | number | null) {
  if (!isTracingEnabled()) {
    return;
  }

  customAttributes = {
    userId: userId ? String(userId) : undefined,
    orgId: orgId ? String(orgId) : undefined,
  };
}

const isDev = import.meta.env.MODE === 'development';

const setupTelemetry = () => {
  if (!isTracingEnabled()) {
    if (isDev) {
      console.log('Tracing disabled — neither VITE_ENABLE_TRACING nor Faro are active');
    }
    return;
  }

  if (isDev) {
    console.log(`Tracing enabled — OTLP: ${isOtlpEnabled}, Faro: ${isFaroEnabled}`);
  }

  try {
    const spanProcessors: SpanProcessor[] = [new CustomAttributesSpanProcessor()];

    if (isFaroEnabled && faro) {
      spanProcessors.push(
        new FaroUserActionSpanProcessor(
          new FaroMetaAttributesSpanProcessor(
            new BatchSpanProcessor(new FaroTraceExporter({ api: faro.api })),
            faro.metas,
          ),
        ),
      );
    }

    if (isOtlpEnabled) {
      const exporterUrl = import.meta.env.VITE_TRACING_OTLP_TRACE_EXPORTER_URL;
      spanProcessors.push(new BatchSpanProcessor(new OTLPTraceExporter({ url: exporterUrl, headers: {} })));
    }

    const provider = new WebTracerProvider({
      resource: resourceFromAttributes({
        [ATTR_SERVICE_NAME]: 'predium-frontend',
      }),
      spanProcessors,
    });

    provider.register({
      contextManager: new ZoneContextManager(),
    });

    const faroCollectorUrl = import.meta.env.VITE_FARO_COLLECTOR_URL;
    const mapsProxyDomain = import.meta.env.VITE_MAPS_PROXY_DOMAIN;
    const posthogProxyHost = import.meta.env.VITE_POSTHOG_PROXY_HOST;
    const ignoreUrls: (string | RegExp)[] = [
      // Auth0 (custom domain) doesn't allow `traceparent` in its CORS preflight, so propagating
      // trace headers to /oauth/token breaks the auth flow and causes a login/logout loop.
      /https?:\/\/auth(\.[a-z-]+)?\.predium\.de/,
    ];
    if (!isNil(faroCollectorUrl)) ignoreUrls.push(new RegExp(faroCollectorUrl));
    // The maps proxy forwards to upstream (MapTiler) which doesn't allow `traceparent` in CORS,
    // so propagating trace headers there breaks preflights — exclude from instrumentation.
    if (!isNil(mapsProxyDomain)) ignoreUrls.push(new RegExp(`https?://${mapsProxyDomain.replace(/\./g, '\\.')}`));
    // The PostHog proxy lives under predium.de, so it would match propagateTraceHeaderCorsUrls;
    // PostHog doesn't allow `traceparent` in its CORS preflight — exclude it.
    if (!isNil(posthogProxyHost) && posthogProxyHost !== '') {
      ignoreUrls.push(new RegExp(posthogProxyHost.replace(/\./g, '\\.')));
    }

    registerInstrumentations({
      instrumentations: [
        new DocumentLoadInstrumentation(),
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: [
            /https?:\/\/.*\.predium\.de/,
            /https?:\/\/localhost/,
            /https?:\/\/127\.0\.0\.1/,
          ],
          ignoreUrls,
          ignoreNetworkEvents: true,
        }),
      ],
    });

    if (isFaroEnabled && faro) {
      faro.api.initOTEL(trace, context);
    }
  } catch (error) {
    console.error('Failed to initialize OpenTelemetry tracing:', error);
  }
};

setupTelemetry();
